import type { PlaybackTarget } from './player.js';
import {
  HARD_SYNC_THRESHOLD_SEC,
  SOFT_SYNC_THRESHOLD_SEC,
} from './player.js';

/**
 * `<audio>` 要素で再生するプレイヤー。
 *
 * YouTube の埋め込みプレイヤーはスマホで画面を消したりアプリを切り替えると
 * 再生を止める。ブラウザが音声そのものを握っていれば OS がメディア再生として
 * 扱うため、画面を消しても別アプリへ移っても鳴り続ける。音声はサーバが
 * `/api/audio/:videoId` で中継してくる。
 *
 * 連続再生の要点は「最初の1回だけユーザー操作で play() すること」。
 * 以降は src を差し替えて play() を呼べば、操作なしで次の曲へ進める。
 * iOS はこの「解錠」を経ていないと自動再生を拒否する。
 */

const NUDGE_RATE = 0.03;
const LOOP_INTERVAL_MS = 1000;
/** 読み込み直後は位置が安定しないので補正を控える */
const SETTLE_MS = 1500;

export type MediaInfo = {
  title: string;
  artist: string | null;
  artwork: string | null;
};

export type AudioPlayerOptions = {
  container: HTMLElement;
  getTarget: () => PlaybackTarget | null;
  shouldCorrect: () => boolean;
  onPosition: (trackId: string, positionMs: number) => void;
  onMeta: (meta: { trackId: string; durationMs: number }) => void;
  onEnded: (trackId: string) => void;
  onUnplayable: (trackId: string, code: number) => void;
  onDriftChange?: (driftSec: number) => void;
  /** ロック画面に出す曲情報 */
  getMediaInfo: () => MediaInfo | null;
  /** ロック画面やイヤホンのボタンからの操作 */
  onCommand: (command: 'play' | 'pause' | 'next') => void;
};

export class AudioPlayer {
  private readonly audio: HTMLAudioElement;
  private loopTimer: number | null = null;
  private loadedTrackId: string | null = null;
  private settleUntilMs = 0;
  private reportedMetaFor: string | null = null;
  private mediaInfoKey = '';
  private disposed = false;

  private constructor(private readonly opts: AudioPlayerOptions) {
    this.audio = document.createElement('audio');
    this.audio.preload = 'auto';
    // 画面に出す必要はないが、DOM から外すと一部ブラウザで再生が止まる
    this.audio.style.display = 'none';
    opts.container.appendChild(this.audio);
  }

  /**
   * 必ずユーザー操作（「音を出す」トグル）から呼ぶこと。
   * ここでの play() が以降の自動再生を許可する鍵になる。
   */
  static async create(opts: AudioPlayerOptions): Promise<AudioPlayer> {
    const player = new AudioPlayer(opts);
    player.attachEvents();
    player.setupMediaSession();

    const target = opts.getTarget();
    if (target) player.load(target);
    // 曲がまだ無くても、無音を一度再生して要素を解錠しておく
    try {
      await player.audio.play();
    } catch {
      // 曲が未設定なら失敗する。曲が入った時点で改めて再生する。
    }

    player.loopTimer = window.setInterval(() => player.tick(), LOOP_INTERVAL_MS);
    return player;
  }

  private attachEvents(): void {
    this.audio.addEventListener('ended', () => {
      if (this.disposed) return;
      const trackId = this.loadedTrackId;
      if (trackId) this.opts.onEnded(trackId);
    });

    this.audio.addEventListener('loadedmetadata', () => {
      if (this.disposed) return;
      this.reportMetaIfReady();
    });

    this.audio.addEventListener('error', () => {
      if (this.disposed) return;
      const trackId = this.loadedTrackId;
      const code = this.audio.error?.code ?? 0;
      if (trackId) this.opts.onUnplayable(trackId, code);
    });
  }

  /**
   * ロック画面と通知領域に曲情報と操作ボタンを出す。
   * これがあると OS がメディア再生として扱い、バックグラウンドでも
   * 止められにくくなる。
   */
  private setupMediaSession(): void {
    const session = navigator.mediaSession;
    if (!session) return;
    const set = (
      action: MediaSessionAction,
      handler: MediaSessionActionHandler,
    ) => {
      try {
        session.setActionHandler(action, handler);
      } catch {
        // 対応していない操作は無視してよい
      }
    };
    set('play', () => this.opts.onCommand('play'));
    set('pause', () => this.opts.onCommand('pause'));
    set('nexttrack', () => this.opts.onCommand('next'));
  }

  private updateMediaSession(): void {
    const session = navigator.mediaSession;
    if (!session) return;
    const info = this.opts.getMediaInfo();
    if (!info) return;
    const key = `${info.title}|${info.artist ?? ''}|${info.artwork ?? ''}`;
    if (key === this.mediaInfoKey) return;
    this.mediaInfoKey = key;
    try {
      session.metadata = new MediaMetadata({
        title: info.title,
        artist: info.artist ?? '',
        artwork: info.artwork ? [{ src: info.artwork, sizes: '512x512' }] : [],
      });
    } catch {
      // MediaMetadata が無い環境では諦める
    }
  }

  private load(target: PlaybackTarget): void {
    this.loadedTrackId = target.trackId;
    this.reportedMetaFor = null;
    this.settleUntilMs = Date.now() + SETTLE_MS;
    this.audio.playbackRate = 1;
    this.audio.src = `/api/audio/${encodeURIComponent(target.videoId)}`;

    const seek = () => {
      this.audio.currentTime = Math.max(0, target.targetSec);
      this.audio.removeEventListener('loadedmetadata', seek);
    };
    if (target.targetSec > 0.5) {
      this.audio.addEventListener('loadedmetadata', seek);
    }
    this.updateMediaSession();
  }

  private tick(): void {
    if (this.disposed) return;
    const target = this.opts.getTarget();

    if (!target) {
      if (!this.audio.paused) this.audio.pause();
      return;
    }

    if (target.trackId !== this.loadedTrackId) {
      this.load(target);
      if (!target.paused) void this.audio.play().catch(() => {});
      return;
    }

    this.updateMediaSession();

    if (target.paused) {
      if (!this.audio.paused) this.audio.pause();
      this.audio.playbackRate = 1;
      return;
    }

    if (this.audio.paused) void this.audio.play().catch(() => {});
    this.reportMetaIfReady();

    // 読み込み直後や、まだ十分に読めていないうちは位置が信用できない
    if (Date.now() < this.settleUntilMs || this.audio.readyState < 2) return;

    const actual = this.audio.currentTime;

    // 合わせる相手がいないならこの端末が基準。補正せず実位置を返すだけ。
    if (!this.opts.shouldCorrect()) {
      this.audio.playbackRate = 1;
      this.opts.onDriftChange?.(0);
      this.opts.onPosition(target.trackId, Math.round(actual * 1000));
      return;
    }

    const drift = target.targetSec - actual;
    this.opts.onDriftChange?.(drift);

    if (Math.abs(drift) >= HARD_SYNC_THRESHOLD_SEC) {
      this.settleUntilMs = Date.now() + SETTLE_MS;
      this.audio.playbackRate = 1;
      this.audio.currentTime = Math.max(0, target.targetSec);
      return;
    }
    if (Math.abs(drift) >= SOFT_SYNC_THRESHOLD_SEC) {
      this.audio.playbackRate = drift > 0 ? 1 + NUDGE_RATE : 1 - NUDGE_RATE;
      return;
    }
    this.audio.playbackRate = 1;
  }

  private reportMetaIfReady(): void {
    const trackId = this.loadedTrackId;
    if (!trackId || this.reportedMetaFor === trackId) return;
    const seconds = this.audio.duration;
    if (!Number.isFinite(seconds) || seconds <= 0) return;
    this.reportedMetaFor = trackId;
    this.opts.onMeta({ trackId, durationMs: Math.round(seconds * 1000) });
  }

  destroy(): void {
    this.disposed = true;
    if (this.loopTimer !== null) window.clearInterval(this.loopTimer);
    this.loopTimer = null;
    try {
      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio.load();
    } catch {
      // 破棄時の失敗は無視してよい
    }
    this.audio.remove();
    const session = navigator.mediaSession;
    if (session) {
      session.metadata = null;
      for (const action of ['play', 'pause', 'nexttrack'] as const) {
        try {
          session.setActionHandler(action, null);
        } catch {
          // 対応していない操作は無視してよい
        }
      }
    }
  }
}
